import { useDelegationStore } from "@/stores/delegation-store";
import {
  CreateLocationRequest,
  CreateSiteRequest,
  // Keep old type exports for backwards compatibility
  Location,
  LocationsGroupedResponse,
  LocationTypeOption,
  Site,
  SitesGroupedResponse,
  SiteTypeOption,
  UpdateLocationRequest,
  UpdateSiteRequest,
} from "@/types/location";
import { apiClient } from "./api";

// Helper to get delegation parameters
const getDelegationParams = () => {
  const delegationParam = useDelegationStore.getState().delegationParam;
  return delegationParam ? { delegateAs: delegationParam } : {};
};

// Transform backend Site response to frontend Site format
const transformSiteResponse = (site: any): Site => ({
  id: site.id,
  organizationId: site.organization_id,
  name: site.name,
  siteType: site.site_type,
  description: site.description,
  address: site.address,
  coordinates: site.coordinates || { lat: 0, lng: 0 },
  city: site.city,
  country: site.country,
  postalCode: site.postal_code,
  metadata: site.metadata,
  settings: site.settings,
  status: site.status,
  deviceCount: site.device_count || 0,
  createdAt: site.created_at,
  updatedAt: site.updated_at,
  createdBy: site.created_by,
});

// Transform frontend request to backend format
const transformSiteRequest = (
  data: CreateSiteRequest | UpdateSiteRequest
): any => ({
  name: data.name,
  site_type: data.siteType,
  organization_id: (data as CreateSiteRequest).organizationId, // For integrator site creation
  description: data.description,
  address: data.address,
  coordinates: data.coordinates,
  city: data.city,
  country: data.country,
  postal_code: data.postalCode,
  metadata: data.metadata,
  settings: data.settings,
  status: (data as UpdateSiteRequest).status,
});

export class SiteAPI {
  // Get all sites grouped by type
  static async getSites(): Promise<SitesGroupedResponse> {
    const response = await apiClient.get("/v1/sites", {
      params: getDelegationParams(),
    });
    const data = response.data;

    // Transform the response
    const sites = (data.sites || []).map(transformSiteResponse);
    const groups: Record<string, Site[]> = {};

    // Transform groups
    if (data.groups) {
      Object.entries(data.groups).forEach(([type, sitesArray]) => {
        groups[type] = (sitesArray as any[]).map(transformSiteResponse);
      });
    }

    return {
      sites,
      groups,
      typeCounts: data.type_counts || {},
      typeOptions: (data.type_options || []).map((opt: any) => ({
        value: opt.value,
        label: opt.label,
        description: opt.description,
      })),
    };
  }

  // Get available site types
  static async getSiteTypes(): Promise<SiteTypeOption[]> {
    const response = await apiClient.get("/v1/sites/types", {
      params: getDelegationParams(),
    });
    return (response.data.types || []).map((opt: any) => ({
      value: opt.value,
      label: opt.label,
      description: opt.description,
    }));
  }

  // Get specific site
  static async getSite(id: string): Promise<Site> {
    const response = await apiClient.get(`/v1/sites/${id}`, {
      params: getDelegationParams(),
    });
    return transformSiteResponse(response.data.site);
  }

  // Create new site
  static async createSite(data: CreateSiteRequest): Promise<Site> {
    const response = await apiClient.post(
      "/v1/sites",
      transformSiteRequest(data),
      { params: getDelegationParams() }
    );
    return transformSiteResponse(response.data.site);
  }

  // Update site
  static async updateSite(id: string, data: UpdateSiteRequest): Promise<Site> {
    const response = await apiClient.put(
      `/v1/sites/${id}`,
      transformSiteRequest(data),
      { params: getDelegationParams() }
    );
    return transformSiteResponse(response.data.site);
  }

  // Delete site
  static async deleteSite(id: string): Promise<void> {
    await apiClient.delete(`/v1/sites/${id}`, {
      params: getDelegationParams(),
    });
  }

  // Get sites summary
  static async getSitesSummary() {
    const response = await this.getSites();

    return response.sites.map((site) => ({
      id: site.id,
      name: site.name,
      siteType: site.siteType,
      address: site.address || "",
      coordinates: site.coordinates,
      deviceCount: site.deviceCount || 0,
      onlineDeviceCount: 0, // Will be updated with real telemetry data
      status: site.status,
      createdAt: site.createdAt,
    }));
  }

  // Get devices at a site
  static async getSiteDevices(siteId: string) {
    const response = await apiClient.get(`/v1/sites/${siteId}/devices`, {
      params: getDelegationParams(),
    });
    return response.data;
  }

  // Add device to site
  static async addDeviceToSite(siteId: string, deviceId: string) {
    const response = await apiClient.post(
      `/v1/sites/${siteId}/devices`,
      { device_id: deviceId },
      { params: getDelegationParams() }
    );
    return response.data;
  }

  // Remove device from site
  static async removeDeviceFromSite(siteId: string, deviceId: string) {
    await apiClient.delete(`/v1/sites/${siteId}/devices/${deviceId}`, {
      params: getDelegationParams(),
    });
  }
}

// Backwards compatibility: LocationAPI wraps SiteAPI with transformed types
export class LocationAPI {
  // Get all locations grouped by type (uses sites endpoint)
  static async getLocations(): Promise<LocationsGroupedResponse> {
    const response = await SiteAPI.getSites();

    // Transform to location format
    const locations = response.sites.map((site) => ({
      id: site.id,
      name: site.name,
      locationType: site.siteType,
      description: site.description,
      address: site.address,
      coordinates: site.coordinates,
      city: site.city,
      country: site.country,
      postalCode: site.postalCode,
      metadata: site.metadata,
      settings: site.settings,
      status: site.status,
      deviceCount: site.deviceCount,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      createdBy: site.createdBy,
    })) as Location[];

    const groups: Record<string, Location[]> = {};
    Object.entries(response.groups).forEach(([type, sites]) => {
      groups[type] = sites.map((site) => ({
        id: site.id,
        name: site.name,
        locationType: site.siteType,
        description: site.description,
        address: site.address,
        coordinates: site.coordinates,
        city: site.city,
        country: site.country,
        postalCode: site.postalCode,
        metadata: site.metadata,
        settings: site.settings,
        status: site.status,
        deviceCount: site.deviceCount,
        createdAt: site.createdAt,
        updatedAt: site.updatedAt,
        createdBy: site.createdBy,
      })) as Location[];
    });

    return {
      locations,
      groups,
      typeCounts: response.typeCounts,
      typeOptions: response.typeOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        description: opt.description,
      })),
    };
  }

  // Get available location types
  static async getLocationTypes(): Promise<LocationTypeOption[]> {
    return SiteAPI.getSiteTypes();
  }

  // Get specific location
  static async getLocation(id: string): Promise<Location> {
    const site = await SiteAPI.getSite(id);
    return {
      id: site.id,
      name: site.name,
      locationType: site.siteType,
      description: site.description,
      address: site.address,
      coordinates: site.coordinates,
      city: site.city,
      country: site.country,
      postalCode: site.postalCode,
      metadata: site.metadata,
      settings: site.settings,
      status: site.status,
      deviceCount: site.deviceCount,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      createdBy: site.createdBy,
    };
  }

  // Create new location
  static async createLocation(data: CreateLocationRequest): Promise<Location> {
    const site = await SiteAPI.createSite({
      name: data.name,
      siteType: data.locationType,
      organizationId: data.organizationId,
      description: data.description,
      address: data.address,
      coordinates: data.coordinates,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode,
      metadata: data.metadata,
      settings: data.settings,
    });
    return {
      id: site.id,
      name: site.name,
      locationType: site.siteType,
      description: site.description,
      address: site.address,
      coordinates: site.coordinates,
      city: site.city,
      country: site.country,
      postalCode: site.postalCode,
      metadata: site.metadata,
      settings: site.settings,
      status: site.status,
      deviceCount: site.deviceCount,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      createdBy: site.createdBy,
    };
  }

  // Update location
  static async updateLocation(
    id: string,
    data: UpdateLocationRequest
  ): Promise<Location> {
    const site = await SiteAPI.updateSite(id, {
      name: data.name,
      siteType: data.locationType,
      description: data.description,
      address: data.address,
      coordinates: data.coordinates,
      city: data.city,
      country: data.country,
      postalCode: data.postalCode,
      status: data.status,
      metadata: data.metadata,
      settings: data.settings,
    });
    return {
      id: site.id,
      name: site.name,
      locationType: site.siteType,
      description: site.description,
      address: site.address,
      coordinates: site.coordinates,
      city: site.city,
      country: site.country,
      postalCode: site.postalCode,
      metadata: site.metadata,
      settings: site.settings,
      status: site.status,
      deviceCount: site.deviceCount,
      createdAt: site.createdAt,
      updatedAt: site.updatedAt,
      createdBy: site.createdBy,
    };
  }

  // Delete location
  static async deleteLocation(id: string): Promise<void> {
    await SiteAPI.deleteSite(id);
  }

  // Helper: Get locations summary
  static async getLocationsSummary() {
    const response = await this.getLocations();

    return response.locations.map((location) => ({
      id: location.id,
      name: location.name,
      locationType: location.locationType,
      address: location.address || "",
      coordinates: location.coordinates,
      deviceCount: location.deviceCount || 0,
      onlineDeviceCount: 0, // Will be updated with real telemetry data
      status: location.status,
      createdAt: location.createdAt,
    }));
  }
}
